<template>
  <div class="d-flex fill-height flex-column">
    <ActionBar
      class="pa-3"
      border-bottom
      show-add
      show-generate
      @generate="generateButtonClicked"
      :loading-generate="generateLoading"
      show-save
      :loading-save="loading"
      @add="add()"
      @save="saveChanges()"
      :disabled="isLocked || soilLayerItems.length === 0"
    >
    </ActionBar>
    <v-data-table
      class="state-table fill-height ma-0"
      :headers="headers"
      density="compact"
      disable-sort
      hide-no-data
      hide-default-footer
    >
      <template #bottom></template>
      <template v-slot:tbody>
        <NoDataInDataTable class="mt-3" :colspan="500" :show="showNoData" />
        <tbody>
          <tr
            @contextmenu.prevent="isLocked || show($event, item)"
            v-for="(item, idx) in stateItems"
            :key="idx"
            @click="$emit('setActiveItem', idx)"
          >
            <td :style="`border-left: solid 5px ${getFocusLineColor(idx)}`">
              <span style="padding-left: 5px">{{ idx + 1 }}</span>
            </td>
            <td>
              <DropDownSoilLayerSelect
                :show="soilLayerItems.length && baseSoilTypeItems.length"
                :options="soilLayerItems"
                :used-items="usedSoilLayers"
                :item="item"
                :selected-soil-layer="item ? item.properties.soil_layer : null"
                parameter="properties.soil_layer"
                :name="baseSoilTypeItems"
                :class="[
                  getDirtyClass(item, 'properties.soil_layer'),
                  isLocked ? 'disable-events' : ''
                ]"
                @update="addDirty(item, 'properties.soil_layer')"
                @set="setParameter"
              />
            </td>
            <td v-for="(header, index) in visibleHeaders" :key="index">
              <DataTableField
                :item="item"
                width="75px"
                :parameter="header.value"
                :editable="header.editable"
                :type="header.type"
                :class="[getDirtyClass(item, header.value), isLocked ? 'disable-events' : '']"
                @set="setParameter"
                @update="addDirty(item, header.value)"
              />
            </td>
            <td>
              <span style="padding-left: 5px">{{ getPopCharacteristic(item) }}</span>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <TableActionMenu
      @update:show-menu="showMenu = $event"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      :addButton="false"
      @action="deleteObj(selectedItem.id)"
    />
    <StateGenerateDialog
      :model-value="generateDialog"
      :loading="generateLoading"
      @generate="dialogGenerateButtonClicked"
      @close="toggleDialog"
    />
  </div>
</template>

<script>
import DataTableField from '@/components/form/DataTableField.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import DropDownSoilLayerSelect from '@/components/state/DropDownSoilLayerSelect.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import ShowActionMenuMixin from '@/mixins/ShowActionMenuMixin.vue'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import NoDataInDataTable from '@/components/common/NoDataInDataTable.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import StateGenerateDialog from '@/components/state/StateGenerateDialog.vue'

export default {
  name: 'StatesTable',
  components: {
    StateGenerateDialog,
    DataTableField,
    DropDownSoilLayerSelect,
    TableActionMenu,
    NoDataInDataTable,
    ActionBar
  },
  props: {
    hoverPoint: {}
  },
  mixins: [DirtyFieldsMixIn, ShowActionMenuMixin],
  data: () => ({
    headers: [
      {
        title: '#',
        value: 'id',
        editable: false,
        type: 'number',
        width: '5%',
        align: 'center'
      },
      {
        title: 'Soil layer',
        value: 'soil_layer',
        editable: false,
        type: 'number',
        width: '35%'
      },
      {
        title: 'X',
        value: 'geometry.coordinates.0',
        editable: true,
        type: 'number',
        width: '8%',
        field: true
      },
      {
        title: 'Z',
        value: 'geometry.coordinates.1',
        editable: true,
        type: 'number',
        width: '8%',
        field: true
      },
      {
        title: 'Name',
        value: 'properties.name',
        editable: true,
        type: 'string',
        width: '30%',
        field: true
      },
      {
        title: 'State',
        value: 'properties.state',
        editable: false,
        type: 'string',
        width: '8%',
        field: true,
        padding: '0 0 0 40px'
      },
      {
        title: 'POP',
        value: 'type',
        editable: false,
        type: 'number',
        width: '5%'
      }
    ]
  }),
  computed: {
    ...mapState('statePoints', {
      stateItems: (state) => state.items,
      statePointColor: (state) => state.color,
      loading: (state) => state.loading,
      generateLoading: (state) => state.generateLoading,
      generateDialog: (state) => state.dialog
    }),
    ...mapState('soilLayer', {
      soilLayerItems: (state) => state.items
    }),
    ...mapState('baseSoilType', {
      baseSoilTypeItems: (state) => state.items
    }),
    ...mapState('soilType', {
      soilTypeItemsOverview: (state) => state.itemsOverview
    }),
    ...mapState('stabSchematisation', {
      currentStabSchematisation: (state) => state.currentItem
    }),
    ...mapState('stabSchematisationStage', {
      currentStabSchematisationStage: (state) => state.currentItem
    }),
    ...mapGetters('stabSchematisation', {
      isLocked: 'isLockedCurrent'
    }),
    visibleHeaders() {
      return this.headers.filter((header) => header.field === true)
    },
    usedSoilLayers() {
      return this.stateItems ? this.stateItems.map((item) => item.properties.soil_layer) : []
    },
    showNoData() {
      return this.stateItems ? this.stateItems.length === 0 : true
    }
  },
  methods: {
    ...mapActions('statePoints', {
      update: 'update',
      create: 'create',
      setParameter: 'setNestedParameter',
      emptyDirtyItems: 'emptyDirtyItems',
      add: 'add',
      deleteObj: 'deleteItem',
      generate: 'generate',
      toggleDialog: 'toggleDialog'
    }),
    async generateStatePointsForCurrentStage() {
      await this.generate({
        schematisationStageId: this.currentStabSchematisationStage.id,
        soilLayerId: this.soilLayerItems[0].id
      })
    },
    async dialogGenerateButtonClicked() {
      await this.generateStatePointsForCurrentStage()
      this.toggleDialog()
    },
    async generateButtonClicked() {
      if (this.stateItems.length > 0) {
        this.toggleDialog()
      } else {
        await this.generateStatePointsForCurrentStage()
      }
    },
    async saveChanges() {
      try {
        await this.update()
        await this.create()
        this.emptyDirtyItems()
        this.dirtyItems = []
      } catch {
        null
      }
    },
    getFocusLineColor(index) {
      return this.hoverPoint && this.hoverPoint.pointIndex === index
        ? this.statePointColor
        : 'white'
    },
    getPopCharacteristic(statePoint) {
      let refSoilLayer = this.soilLayerItems.find(
        (item) => statePoint.properties.soil_layer === item.id
      )
      let refSoilType
      if (refSoilLayer) {
        refSoilType = this.baseSoilTypeItems.find(
          (item) => refSoilLayer.properties.base_soil_type === item.id
        )
      }
      const soilType = this.soilTypeItemsOverview.find(
        (item) =>
          item.base_soil_type === refSoilType?.id &&
          item.soil_type_revision === this.currentStabSchematisation.soil_type_revision.id
      )
      return soilType && soilType['pop_characteristic'] ? soilType['pop_characteristic'] : null
    }
  }
}
</script>
<style scoped>
.state-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: unset !important;
}

.state-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 0 0 0 4px !important;
}
:deep(th) {
  padding: 0 5px !important;
}
.state-table {
  :deep(td) {
    padding: 0 !important;
  }
}
</style>
